import * as m from "@paraglide/messages"
import type { SupportedLocale } from "@playbrain/content-types"
import { playdataButtonClassName } from "@playdata/PlaydataPrimitives"
import { useState } from "react"

/**
 * The newsletter signup, as the live site has it.
 *
 * Ours offered a button through to the contact page, which put two CTAs
 * back to back and asked a reader who only wanted the newsletter to write a
 * whole enquiry. This takes the address where the live form takes it.
 *
 * It posts to the same `/api` mailer proxy the contact form uses. The mailer
 * routes an unrecognised `type` to the contact inbox, which is where a
 * subscription request should land — nothing here needs a list service that
 * the site does not have.
 */
export function PlaydataNewsletterForm({ locale }: { locale: SupportedLocale }) {
  const [email, setEmail] = useState("")
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle")

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (state === "sending") return
    setState("sending")

    const subject = `${m.playdata_mail_type_newsletter({}, { locale })}: ${email}`
    try {
      const response = await fetch("/api", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: m.playdata_mail_type_newsletter({}, { locale }),
          email,
          mailSubjectToUser: subject,
          mailSubjectToPB: subject,
          message: `Newsletter signup from the PLAYDATA microsite (${locale}).`,
        }),
      })
      setState(response.ok ? "sent" : "failed")
      if (response.ok) setEmail("")
    } catch {
      setState("failed")
    }
  }

  if (state === "sent") {
    return (
      <p
        className="text-[1.5rem]"
        data-id="playdata-newsletter-success"
        role="status"
      >
        {m.playdata_home_newsletter_thanks({}, { locale })}
      </p>
    )
  }

  return (
    <form
      className="flex w-full max-w-[46rem] flex-col gap-[1.2rem]"
      data-id="playdata-newsletter-form"
      onSubmit={handleSubmit}
    >
      <label
        className="text-left text-[1.3rem]"
        htmlFor="playdata-newsletter-email"
      >
        {m.playdata_home_newsletter_emailLabel({}, { locale })}
      </label>
      <input
        autoComplete="email"
        className="w-full border border-[#c9cfcf] bg-play-white px-[1.2rem] py-[1rem] text-[1.5rem]"
        id="playdata-newsletter-email"
        name="email"
        onChange={(event) => setEmail(event.target.value)}
        required
        type="email"
        value={email}
      />
      {state === "failed" ? (
        <p
          className="text-left text-[1.3rem] text-play-pink"
          role="alert"
        >
          {m.playdata_home_newsletter_error({}, { locale })}
        </p>
      ) : null}
      <button
        className={playdataButtonClassName("primary", "self-center disabled:opacity-70")}
        disabled={state === "sending"}
        type="submit"
      >
        {state === "sending"
          ? m.playdata_home_newsletter_sending({}, { locale })
          : m.playdata_home_newsletter_submit({}, { locale })}
      </button>
    </form>
  )
}
